import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Quote, Star } from "lucide-react";
import { cn, renderDisplayText } from "@/lib/utils";
import { useParallax, useCountUp, useInView, decimalPlaces } from "@/hooks/useTenantMotion";
import { LoadingLabel, MediaCardSkeleton, ReviewCardSkeleton, Skel } from "@/components/home/TenantSkeletons";
import { GoogleGlyph } from "@/components/reviews/GoogleGlyph";
import type { ReviewsModel, ServicesModel, TrustItem } from "@/components/home/layouts/models";
import {
  Avatar, HeroActions, HeroCopySkeleton, HeroTitle, Photo, RatingLine, ReviewExtras, ReviewerLine, StarRow,
  type HeroViewProps,
} from "@/components/home/layouts/shared";

/** Classic Professional: balanced split hero, photo cards, testimonial cards. */
export function ClassicHero({ m, edit }: HeroViewProps) {
  const mediaRef = useRef<HTMLDivElement>(null);
  useParallax(mediaRef, 0.06);
  const seq = (index: number) => ({ ["--seq" as string]: index });
  return (
    <section className="relative -mt-20 overflow-hidden pb-16 pt-28 md:pb-24 md:pt-[136px] lg:pb-28" aria-busy={!m.ready}>
      <div className="tp-hero-wash" aria-hidden />
      <div className="container relative mx-auto px-5 md:px-6">
        <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-6">
            {m.ready ? (
              <>
                <p className="tp-eyebrow tp-seq" style={seq(0)}>{m.eyebrow}</p>
                <HeroTitle m={m} edit={edit} className="tp-display tp-seq mt-6 md:mt-7" />
                <div className="tp-lead tp-seq mt-6 max-w-[34rem] md:mt-7" style={seq(2)}>
                  {edit?.subtitle ?? <p>{m.subtitle}</p>}
                </div>
                <div className="tp-seq mt-9 md:mt-10" style={seq(3)}>
                  <HeroActions m={m} edit={edit} />
                </div>
              </>
            ) : (
              <HeroCopySkeleton />
            )}
            <div className="tp-seq mt-10 border-t tp-hairline pt-7" style={seq(4)}>
              <RatingLine m={m} />
            </div>
          </div>

          <div className="relative lg:col-span-6 lg:pl-6 xl:pl-10">
            <div ref={mediaRef} className="tp-media tp-hero-media tp-shadow-lg relative aspect-[4/3.2] md:aspect-[5/4.4]">
              {m.heroPending ? (
                <div className="tp-skeleton absolute inset-0"><Skel className="h-full w-full rounded-none" /></div>
              ) : (
                <div className="tp-parallax">
                  {edit?.image ?? <Photo src={m.heroImage} alt="Professional cleaner at work" fetchPriority="high" decoding="async" />}
                </div>
              )}
            </div>
            <div className="tp-glass tp-hero-float absolute -bottom-6 left-4 flex items-center gap-4 rounded-2xl px-5 py-4 md:-left-6 md:bottom-10">
              {m.reviewsPending ? (
                <div className="tp-skeleton flex items-center gap-4" aria-hidden>
                  <Skel className="h-9 w-14 rounded-lg" />
                  <Skel className="h-3 w-24 tp-skel-round" />
                </div>
              ) : (
                <>
                  <span className="tp-numeral text-[2.1rem] text-foreground">{m.rating.toFixed(1)}</span>
                  <span className="leading-tight">
                    <StarRow className="text-sm" />
                    <span className="mt-1 block text-xs font-medium text-muted-foreground">Average customer rating</span>
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ClassicTrust({ items }: { items: TrustItem[] }) {
  return (
    <section aria-label="Why customers trust us" className="relative border-y tp-hairline bg-card/60">
      <div className="container mx-auto px-5 md:px-6">
        <ul className={cn("grid grid-cols-1 sm:grid-cols-2", items.length >= 4 ? "lg:grid-cols-4" : "lg:grid-cols-3")}>
          {items.map(({ key, Icon, title, subtitle }, index) => (
            <li
              key={key}
              className={cn(
                "flex items-center gap-4 py-5 sm:py-7 lg:px-8 lg:first:pl-0",
                index > 0 && "border-t tp-hairline sm:border-t-0",
                index % 2 === 1 && "sm:border-l sm:pl-6",
                index >= 2 && "sm:border-t lg:border-t-0",
                index > 0 && "lg:border-l",
              )}
              data-reveal
              data-hover-lift
              style={{ ["--reveal-delay" as string]: `${index * 80}ms` }}
            >
              <span className="tp-icon-chip"><Icon className="h-5 w-5" strokeWidth={1.75} /></span>
              <span className="min-w-0 leading-tight">
                {title && <span className="block text-[0.95rem] font-semibold text-foreground">{title}</span>}
                {subtitle && <span className="mt-1 block text-sm text-muted-foreground">{subtitle}</span>}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function ClassicServices({ s }: { s: ServicesModel }) {
  const count = s.items.length;
  const gridCols =
    count === 1 ? "md:grid-cols-1 max-w-xl" :
    count === 2 ? "md:grid-cols-2" :
    count === 4 ? "md:grid-cols-2 xl:grid-cols-4" :
    "md:grid-cols-2 lg:grid-cols-3";
  return (
    <section className="tp-section">
      <div className="container mx-auto px-5 md:px-6">
        <div className="grid gap-6 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <p className="tp-eyebrow" data-reveal>What we do</p>
            <h2 className="tp-h2 mt-5" data-reveal style={{ ["--reveal-delay" as string]: "70ms" }}>{renderDisplayText(s.title)}</h2>
          </div>
          <div className="lg:col-span-5 lg:pb-1" data-reveal style={{ ["--reveal-delay" as string]: "140ms" }}>
            <p className="tp-lead">{renderDisplayText(s.lead)}</p>
            <Link to={s.servicesHref} className="tp-link mt-5 text-sm">View all services <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
        {s.loading ? (
          <div className="tp-skeleton mt-12 grid grid-cols-1 gap-5 md:mt-16 md:grid-cols-2 md:gap-6 lg:grid-cols-3" aria-busy="true">
            <LoadingLabel>Loading services</LoadingLabel>
            {[0, 1, 2].map((i) => <div key={i} className={cn(i === 2 && "hidden lg:block")}><MediaCardSkeleton /></div>)}
          </div>
        ) : (
          <div className={cn("mt-12 grid grid-cols-1 gap-5 md:mt-16 md:gap-6", gridCols)}>
            {s.items.map((item, index) => (
              <Link
                key={item.key}
                to={s.servicesHref}
                className="tp-card tp-card--interactive group flex flex-col p-2.5"
                data-reveal
                style={{ ["--reveal-delay" as string]: `${index * 90}ms` }}
              >
                <div className="tp-media tp-media--inner relative aspect-[4/3]">
                  <Photo src={item.image} alt={item.category} className="tp-zoom" loading="lazy" />
                </div>
                <div className="flex flex-1 items-end justify-between gap-4 px-3 pb-3 pt-5">
                  <div className="min-w-0">
                    <h3 className="tp-h3">{renderDisplayText(item.title)}</h3>
                    <p className="mt-1.5 text-sm font-medium text-muted-foreground">{item.price}</p>
                    {item.description && <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.description}</p>}
                  </div>
                  <span className="tp-arrow-chip" aria-hidden><ArrowUpRight className="h-4 w-4" /></span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function ClassicReviews({ r, reviewFormHref }: { r: ReviewsModel; reviewFormHref?: string }) {
  const [summaryRef, inView] = useInView<HTMLDivElement>({ threshold: 0.4 });
  const started = inView && !r.pending;
  const shown = useCountUp(Number(r.average.toFixed(1)), started, { duration: 1300, decimals: Math.max(1, decimalPlaces(r.average.toFixed(1))) });
  const full = Math.round(r.average);
  const google = r.google;
  return (
    <section className="tp-section tp-tint" aria-busy={r.pending}>
      <div className="container mx-auto px-5 md:px-6">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-10">
          <div ref={summaryRef} className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <p className="tp-eyebrow" data-reveal>Reviews</p>
              <h2 className="tp-h2 mt-5" data-reveal style={{ ["--reveal-delay" as string]: "70ms" }}>What our customers say</h2>
              <div className="mt-8 flex items-end gap-4" data-reveal style={{ ["--reveal-delay" as string]: "140ms" }}>
                {r.pending ? (
                  <div className="tp-skeleton flex items-end gap-4" aria-hidden>
                    <Skel className="h-[3.75rem] w-28 rounded-xl md:h-[4.5rem]" />
                    <Skel className="h-5 w-28 tp-skel-round" />
                  </div>
                ) : (
                  <>
                    <span className="tp-numeral text-[3.75rem] text-foreground md:text-[4.5rem]" aria-label={`${r.average.toFixed(1)} out of 5`}>{shown}</span>
                    <div className="pb-2">
                      <div className="tp-stars" aria-hidden>
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Star key={i} strokeWidth={0} className="tp-star h-5 w-5 fill-current" data-on={started && i <= full ? "true" : "false"} style={{ ["--star" as string]: i - 1 }} />
                        ))}
                      </div>
                      <p className="mt-1.5 text-sm">
                        <span className="font-semibold text-foreground">{r.label}</span>
                        <span className="text-muted-foreground"> · {r.countLine}</span>
                      </p>
                    </div>
                  </>
                )}
              </div>
              {!r.pending && google && !r.googleOnly && google.place.rating != null && (
                <a href={google.place.reviewsUrl} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 text-sm hover:underline" data-testid="google-rating-summary">
                  <GoogleGlyph className="h-4 w-4" />
                  <StarRow n={google.place.rating} className="text-sm" />
                  <span><span className="font-semibold text-foreground">{google.place.rating.toFixed(1)}</span><span className="text-muted-foreground"> · {google.place.reviewCount.toLocaleString()} reviews on Google</span></span>
                </a>
              )}
              <p className="mt-6 max-w-xs text-muted-foreground" data-reveal>{r.subtitle}</p>
              {!r.pending && <ReviewExtras google={google} reviewFormHref={reviewFormHref} className="mt-6" />}
            </div>
          </div>
          {r.pending ? (
            <div className="tp-skeleton grid gap-5 sm:grid-cols-2 lg:col-span-8">
              <LoadingLabel>Loading reviews</LoadingLabel>
              <ReviewCardSkeleton />
              <div className="hidden sm:block"><ReviewCardSkeleton /></div>
            </div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:col-span-8">
              {r.items.map((item, index) => (
                <figure key={item.key} className="tp-card tp-card--interactive flex flex-col p-7 md:p-8" data-reveal data-testid={item.google ? "google-review-card" : undefined} style={{ ["--reveal-delay" as string]: `${(index % 2) * 100}ms` }}>
                  <div className="flex items-start justify-between">
                    <Quote className="h-7 w-7 text-primary/25" strokeWidth={1.5} aria-hidden />
                    {item.google && <GoogleGlyph className="h-5 w-5" />}
                  </div>
                  <blockquote className="mt-5 flex-1 text-[1.02rem] leading-relaxed text-foreground"><p className="line-clamp-6">{item.quote}</p></blockquote>
                  <figcaption className="mt-7 flex items-center gap-3 border-t tp-hairline pt-5">
                    <Avatar item={item} className="h-11 w-11" />
                    <div className="min-w-0 flex-1"><ReviewerLine item={item} /></div>
                    <StarRow n={item.rating} className="text-sm" />
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
